<template>
  <section v-if="promotions.length > 0" class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <div class="text-center mb-12">
        <span class="text-primary font-bold tracking-wider uppercase text-sm">{{ $t('promotions.subtitle') }}</span>
        <h2 class="text-3xl font-bold mt-2 text-gray-900">{{ $t('promotions.title') }}</h2>
      </div>

      <!-- Slider Container which centers if few items, scrolls if many -->
      <div class="w-full overflow-x-auto pb-6 scrollbar-hide">
          <div class="flex gap-6 w-fit mx-auto px-4 min-w-full md:min-w-0 justify-start md:justify-center">
            <div v-for="promo in promotions" :key="promo.id" class="flex-shrink-0 w-80 relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group snap-center">
              
              <!-- Image Header -->
              <div class="h-40 w-full relative overflow-hidden">
                 <img v-if="promo.image_url" :src="promo.image_url" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700">
                 <div v-else class="w-full h-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <span class="material-icons text-white opacity-50 text-6xl">flight</span>
                 </div>
                 
                 <!-- Badge -->
                 <div class="bg-gradient-to-r from-primary to-accent text-white text-xs font-bold px-3 py-1 absolute top-0 right-0 rounded-bl-lg z-10 shadow-sm">
                    {{ promo.is_roundtrip ? $t('promotions.roundtrip') : $t('promotions.oneway') }}
                 </div>
              </div>

              <div class="p-4">
                <!-- Route -->
                <div class="flex items-center justify-between mb-4 mt-2">
                  <div class="text-center flex-1 min-w-0 px-1">
                    <span class="block text-base md:text-lg font-bold text-gray-800 leading-tight">{{ promo.origin }}</span>
                    <span class="text-xs text-gray-500 uppercase tracking-wide">{{ $t('promotions.origin') }}</span>
                  </div>
                  <div class="w-12 flex-shrink-0 flex flex-col items-center justify-center text-gray-900 mx-0.5">
                    <span class="material-icons transform rotate-90 md:rotate-0 text-xl">flight_takeoff</span>
                    <div class="w-full h-px bg-gray-900 my-1 relative">
                        <div class="absolute w-1 h-1 bg-gray-900 rounded-full left-0 top-[-1px]"></div>
                        <div class="absolute w-1 h-1 bg-gray-900 rounded-full right-0 top-[-1px]"></div>
                    </div>
                  </div>
                  <div class="text-center flex-1 min-w-0 px-1">
                    <span class="block text-base md:text-lg font-bold text-gray-800 leading-tight">{{ promo.destination }}</span>
                    <span class="text-xs text-gray-500 uppercase tracking-wide">{{ $t('promotions.destination') }}</span>
                  </div>
                </div>

                <!-- Price -->
                <div class="text-center mb-4 pt-4 border-t border-dashed border-gray-200">
                   <span class="text-xs text-gray-400 font-medium uppercase mb-1 block">{{ $t('promotions.from') }}</span>
                   <span class="text-3xl font-bold text-secondary">${{ formatPrice(promo.price) }}</span>
                   <span class="text-xs text-gray-400 block mt-1">{{ $t('promotions.taxes_included') }}</span>
                </div>

                <!-- Validity -->
                <div class="bg-gray-50 rounded-lg p-3 mb-4 text-center" v-if="promo.valid_until">
                    <p class="text-xs text-gray-500">
                      <span class="font-bold text-gray-700">{{ $t('promotions.validity') }}:</span> 
                      {{ formatDate(promo.valid_from) }} - {{ formatDate(promo.valid_until) }}
                    </p>
                </div>

                <!-- Actions -->
                <div class="text-center flex flex-col space-y-2">
                   <button @click="quoteOnWhatsApp(promo)" class="w-full bg-primary hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-all flex items-center justify-center space-x-2">
                      <span>{{ $t('promotions.quote') }}</span>
                      <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24">
                         <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                   </button>
                   <button @click="showConditions(promo)" class="text-gray-500 text-xs font-medium hover:text-primary hover:underline transition-colors">
                      {{ $t('promotions.view_conditions') }}
                   </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>

    <!-- Conditions Modal -->
    <div v-if="selectedPromo" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" @click.self="selectedPromo = null">
       <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative animate-fade-in-up">
          <button @click="selectedPromo = null" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
             <span class="material-icons">close</span>
          </button>
          
          <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
             <span class="material-icons text-primary mr-2">info</span>
             {{ $t('promotions.conditions_title') }}
          </h3>
          
          <div class="prose prose-sm text-gray-600">
             <p v-if="selectedPromo.show_conditions && !selectedPromo.conditions">
                 {{ $t('promotions.default_conditions') }}
             </p>
             <p v-else style="white-space: pre-line;">
                 {{ selectedPromo.conditions }}
             </p>
          </div>

          <div class="mt-6 text-center">
             <button @click="selectedPromo = null" class="bg-primary text-white px-6 py-2 rounded-lg font-bold hover:bg-opacity-90 transition-all">
                {{ $t('promotions.got_it') }}
             </button>
          </div>
       </div>
    </div>
  </section>
</template>

<script>
import apiClient from '@/services/api';

export default {
  name: 'FlightPromotionsSection',
  data() {
    return {
      promotions: [],
      selectedPromo: null,
      loading: true,
      settings: {}
    }
  },
  async mounted() {
    try {
       // Fetch Promotions
       const response = await apiClient.get('/flight-promotions');
       this.promotions = response.data || [];

       // Fetch Settings for WhatsApp
       const settingsResponse = await apiClient.get('/settings');
       const flatSettings = {};
       Object.values(settingsResponse.data).forEach(group => {
         Object.assign(flatSettings, group);
       });
       this.settings = flatSettings;

    } catch (error) {
       console.error("Error loading data", error);
    } finally {
       this.loading = false;
    }
  },
  methods: {
    formatPrice(value) {
        if (!value) return '0.00';
        return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(this.$i18n.locale === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    showConditions(promo) {
        this.selectedPromo = promo;
    },
    quoteOnWhatsApp(promo) {
        if (!this.settings.social_whatsapp) {
            alert(this.$t('header.whatsapp_not_configured'));
            return;
        }
        
        const phone = this.settings.social_whatsapp.replace(/[^0-9]/g, '');
        const text = `Me interesa saber acerca de la promo ${promo.origin} - ${promo.destination}`;
        const encodedText = encodeURIComponent(text);
        
        window.open(`https://wa.me/${phone}?text=${encodedText}`, '_blank');
    }
  }
}
</script>

<style scoped>
.animate-fade-in-up {
    animation: fadeInUp 0.3s ease-out;
}
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
